import { useRef, KeyboardEvent, ClipboardEvent } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (val: string) => void;
  length?: number;
  error?: boolean;
  disabled?: boolean;
  onComplete?: (val: string) => void;
}

/**
 * Stripe-style OTP input — 6 individual boxes with auto-focus, backspace
 * navigation, and paste support.
 */
export function OtpInput({ value, onChange, length = 6, error, disabled, onComplete }: OtpInputProps) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const focusInput = (index: number) => {
    inputs.current[index]?.focus();
  };

  const commitValue = (nextValue: string) => {
    const normalized = nextValue.replace(/\D/g, '').slice(0, length);
    onChange(normalized);
    if (normalized.length === length) {
      onComplete?.(normalized);
    }
    return normalized;
  };

  const distributeDigits = (digits: string) => {
    const normalized = commitValue(digits);
    if (!normalized) return;

    const nextFocusIndex = Math.min(
      normalized.length < length ? normalized.length : length - 1,
      length - 1
    );
    focusInput(nextFocusIndex);
  };

  const handleChange = (index: number, val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, length);
    if (!digits && val) return;

    if (digits.length > 1) {
      distributeDigits(digits);
      return;
    }

    const chars = value.padEnd(length, ' ').split('');
    chars[index] = digits || ' ';
    const final = commitValue(chars.join('').replace(/ /g, ''));

    if (digits && index < length - 1 && final.length <= length) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    distributeDigits(e.clipboardData.getData('text'));
  };

  return (
    <div onPaste={handlePaste} className="relative flex justify-center gap-1.5 sm:gap-2">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        value={value}
        onChange={(e) => distributeDigits(e.target.value)}
        className="pointer-events-none absolute h-px w-px opacity-0"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          className={`
            h-10 w-10 sm:h-12 sm:w-12 text-center text-base sm:text-lg font-semibold rounded-md border
            bg-background text-foreground
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-150 focus:scale-105
            ${error ? 'border-destructive ring-1 ring-destructive' : 'border-input'}
          `}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
